/**
 * Fenda Studio - Main Theme JavaScript
 * Handles header, mobile menu, scroll reveals, and general interactivity
 */

class FendaTheme {
  constructor() {
    this.header = document.querySelector('[data-header]');
    this.menuToggle = document.querySelector('[data-menu-toggle]');
    this.mobileMenu = document.querySelector('[data-mobile-menu]');
    this.searchToggles = document.querySelectorAll('[data-search-toggle]');
    this.priceCache = new Map();
    this.searchModal = document.querySelector('[data-search-modal]');
    this.searchClose = document.querySelector('[data-search-close]');
    this.searchInput = document.querySelector('[data-search-input]');
    
    this.init();
  }

  init() {
    this.initStickyHeader();
    this.initMobileMenu();
    this.initSearch();
    this.initScrollReveal();
    this.initAccordions();
    this.initSmoothScroll();
  }

  // Smooth Scroll for Anchor Links
  initSmoothScroll() {
    // Handle anchor links for cross-page navigation
    document.querySelectorAll('a[href*="#"]').forEach(anchor => {
      anchor.addEventListener('click', (e) => {
        const href = anchor.getAttribute('href');
        
        // Check if it's a same-page anchor or homepage anchor
        if (href.startsWith('#') || href.startsWith('/#')) {
          const targetId = href.includes('#') ? href.split('#')[1] : '';
          
          if (targetId) {
            const targetElement = document.getElementById(targetId);
            
            if (targetElement) {
              e.preventDefault();
              targetElement.scrollIntoView({
                behavior: 'smooth',
                block: 'start'
              });
              
              // Update URL without page reload
              history.pushState(null, '', `#${targetId}`);
            } else if (href.startsWith('/#')) {
              // It's a homepage anchor, let the browser navigate
              // The CSS smooth scroll will handle it
            }
          }
        }
      });
    });

    // Handle initial page load with hash
    if (window.location.hash) {
      const targetId = window.location.hash.substring(1);
      const targetElement = document.getElementById(targetId);
      
      if (targetElement) {
        setTimeout(() => {
          targetElement.scrollIntoView({
            behavior: 'smooth',
            block: 'start'
          });
        }, 100);
      }
    }
  }

  // Sticky Header
  initStickyHeader() {
    if (!this.header) return;
    
    let lastScrollY = window.scrollY;
    
    window.addEventListener('scroll', () => {
      const scrollY = window.scrollY;
      
      if (scrollY > 100) {
        this.header.classList.add('header--scrolled');
      } else {
        this.header.classList.remove('header--scrolled');
      }
      
      lastScrollY = scrollY;
    }, { passive: true });
  }

  // Mobile Menu
  initMobileMenu() {
    if (!this.menuToggle || !this.mobileMenu) return;
    
    this.menuToggle.addEventListener('click', () => {
      const isOpen = this.mobileMenu.classList.toggle('is-open');
      this.menuToggle.classList.toggle('is-open', isOpen);
      document.body.style.overflow = isOpen ? 'hidden' : '';
    });
    
    const closeBtn = this.mobileMenu.querySelector('[data-menu-close]');
    if (closeBtn) {
      closeBtn.addEventListener('click', () => {
        this.mobileMenu.classList.remove('is-open');
        this.menuToggle.classList.remove('is-open');
        document.body.style.overflow = '';
      });
    }
    
    // Close menu when clicking a link
    this.mobileMenu.querySelectorAll('a').forEach(link => {
      link.addEventListener('click', () => {
        this.mobileMenu.classList.remove('is-open');
        this.menuToggle.classList.remove('is-open');
        document.body.style.overflow = '';
      });
    });

    // Mobile submenu toggles
    this.mobileMenu.querySelectorAll('[data-submenu-toggle]').forEach(btn => {
      btn.addEventListener('click', () => {
        const expanded = btn.getAttribute('aria-expanded') === 'true';
        btn.setAttribute('aria-expanded', (!expanded).toString());
        const submenu = btn.nextElementSibling;
        if (submenu && submenu.classList.contains('mobile-submenu')) {
          submenu.classList.toggle('is-open', !expanded);
        }
      });
    });
  }

  // Search Modal
  initSearch() {
    if (!this.searchToggles || !this.searchToggles.length || !this.searchModal) return;
    
    this.searchToggles.forEach(toggle => {
      toggle.addEventListener('click', (e) => {
        if (e && typeof e.preventDefault === 'function') {
          e.preventDefault();
        }
        this.searchModal.classList.add('is-open');
        document.body.style.overflow = 'hidden';
        if (this.searchInput) {
          setTimeout(() => this.searchInput.focus(), 100);
        }
      });
    });
    
    if (this.searchClose) {
      this.searchClose.addEventListener('click', () => {
        this.closeSearch();
      });
    }
    
    // Close on escape
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.searchModal.classList.contains('is-open')) {
        this.closeSearch();
      }
    });
    
    // Close on overlay click
    this.searchModal.addEventListener('click', (e) => {
      if (e.target === this.searchModal) {
        this.closeSearch();
      }
    });
    
    // Predictive search
    if (this.searchInput) {
      this.searchInput.addEventListener('input', this.debounce((e) => {
        this.performSearch(e.target.value);
      }, 300));
    }
  }

  closeSearch() {
    this.searchModal.classList.remove('is-open');
    document.body.style.overflow = '';
  }

  async performSearch(query) {
    if (query.length < 2) {
      this.clearSearchResults();
      return;
    }
    
    try {
      const response = await fetch(`${window.Shopify.routes.root}search/suggest.json?q=${encodeURIComponent(query)}&resources[type]=product&resources[limit]=6`);
      const data = await response.json();
      this.renderSearchResults(data.resources.results.products);
    } catch (error) {
      console.error('Search error:', error);
    }
  }

  renderSearchResults(products) {
    const container = document.querySelector('[data-search-results]');
    if (!container) return;
    
    if (!products || products.length === 0) {
      container.innerHTML = '<p class="text-body" style="padding: 1rem 0;">No products found</p>';
      return;
    }
    
    container.innerHTML = products.map(product => `
      <a href="${product.url}" class="search-result-item">
        ${product.featured_image ? `<img src="${product.featured_image.url}&width=120" alt="${product.title}" class="search-result-item__image">` : ''}
        <div>
          <div class="search-result-item__title">${product.title.toLowerCase()}</div>
          <div class="search-result-item__price" data-price-url="${product.url}" data-raw-price="${product.price || ''}">${this.safeFormatPredictivePrice(product)}</div>
        </div>
      </a>
    `).join('');
    this.hydratePredictivePrices(container);
  }

  clearSearchResults() {
    const container = document.querySelector('[data-search-results]');
    if (container) container.innerHTML = '';
  }

  formatMoney(cents) {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: window.Shopify?.currency?.active || 'EUR'
    }).format(cents / 100);
  }

  safeFormatPredictivePrice(product) {
    const raw = typeof product.price === 'number' ? product.price : parseFloat(product.price || '0');
    if (Number.isFinite(raw) && raw >= 1000) {
      return this.formatMoney(raw);
    }
    if (Number.isFinite(raw) && raw > 0 && raw < 1000) {
      return this.formatMoney(Math.round(raw * 100));
    }
    return '';
  }

  async hydratePredictivePrices(container) {
    const nodes = Array.from(container.querySelectorAll('[data-price-url]'));
    if (!nodes.length) return;
    const tasks = nodes.map(async (el) => {
      const url = el.getAttribute('data-price-url');
      if (!url) return;
      try {
        const cents = await this.getProductCents(url);
        if (typeof cents === 'number' && !Number.isNaN(cents)) {
          el.textContent = this.formatMoney(cents);
        }
      } catch (_) {}
    });
    await Promise.all(tasks);
  }

  async getProductCents(productUrl) {
    if (this.priceCache.has(productUrl)) return this.priceCache.get(productUrl);
    const res = await fetch(productUrl + '.js');
    const data = await res.json();
    const cents = data?.variants && data.variants[0]?.price ? parseInt(data.variants[0].price, 10) : 0;
    this.priceCache.set(productUrl, cents);
    return cents;
  }

  // Scroll Reveal Animation
  initScrollReveal() {
    const reveals = document.querySelectorAll('.reveal');
    
    if (!reveals.length) return;
    
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('revealed');
          observer.unobserve(entry.target);
        }
      });
    }, {
      threshold: 0.1,
      rootMargin: '-50px'
    });
    
    reveals.forEach(el => observer.observe(el));
  }

  // Accordions
  initAccordions() {
    document.querySelectorAll('.accordion-header').forEach(header => {
      header.addEventListener('click', () => {
        const item = header.closest('.accordion-item');
        const isOpen = item.classList.contains('is-open');
        
        // Close other accordions in same group
        const group = item.closest('.accordion-group');
        if (group) {
          group.querySelectorAll('.accordion-item.is-open').forEach(openItem => {
            if (openItem !== item) {
              openItem.classList.remove('is-open');
            }
          });
        }
        
        item.classList.toggle('is-open', !isOpen);
      });
    });
  }

  // Utility: Debounce
  debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }
}

// Initialize theme
document.addEventListener('DOMContentLoaded', () => {
  window.fendaTheme = new FendaTheme();
});
